import { askConfirm } from '@/components/ui/ConfirmHost';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAttemptsById, getTests, getTestsById, postAttemptsEvent, postAttemptsSubmitKeyed, postAttemptsSyncKeyed, postTestsStart } from '@/api/student';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { CbtQuestion, Countdown, QuestionNav, fromAnswer, isAnswered, toAnswer, type CbtAnswer, type CbtQuestionData } from '@/components/ui/Cbt';
import { Banner, Input, Modal, PageHeader, QueryState, Spinner, Table } from '@/components/kit/ui';
import { asList, errMsg, fmtDateTime, unwrap } from '@/components/kit/data';
import { newKey } from '../shared';

interface TestRow { id: number; title: string; kind: string; duration_minutes: number; total_marks: number; status: string; opens_at: string | null; closes_at: string | null; attempts_used: number; max_attempts: number; open_attempt_id: number | null; can_start: boolean; last_attempt_id: number | null }
interface TestDetail extends TestRow { instructions: string | null; requires_code: boolean; question_count: number; show_result: string; allow_resume: boolean }
interface Payload { attempt_id: number; exam_id: number; title: string; instructions: string | null; status: string; total_marks: number; duration_minutes: number; time_left_seconds: number; deadline: string; server_time: string; questions: (CbtQuestionData & { answer: unknown })[] }
type Ev = { event: 'blur' | 'focus' | 'app_background' | 'disconnect' | 'reconnect' | 'copy'; at: string };

function StartDialog({ test, onClose, onStarted }: { test: TestRow; onClose: () => void; onStarted: (p: Payload) => void }) {
  const d = useQuery({ queryKey: ['student-test', test.id], queryFn: () => getTestsById(test.id).then((r) => unwrap<TestDetail>(r)) });
  const [code, setCode] = useState('');
  const [syncUuid] = useState(newKey);
  const start = useMutation({
    mutationFn: () => postTestsStart(test.id, { sync_uuid: syncUuid, device_uuid: localStorage.getItem('ojp-device') ?? undefined, access_code: code || undefined }).then((r) => unwrap<Payload>(r)),
    onSuccess: onStarted,
  });
  const t = d.data;
  return (
    <Modal title={test.title} onClose={onClose}>
      {d.isLoading ? <Spinner /> : d.isError ? <Banner>{errMsg(d.error)}</Banner> : t && (
        <div className="space-y-3 text-sm">
          {t.instructions && <p className="whitespace-pre-wrap">{t.instructions}</p>}
          <p className="text-slate-500">{t.question_count} questions · {t.duration_minutes} minutes · {t.total_marks} marks · attempt {t.attempts_used + (t.open_attempt_id ? 0 : 1)} of {t.max_attempts}</p>
          <p className="text-slate-500">The timer keeps running if you leave the page. Leaving the test window is recorded.</p>
          {t.requires_code && !t.open_attempt_id && <Input label="Test code from your teacher" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 20))} />}
          {start.isError && <Banner>{errMsg(start.error)}</Banner>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button disabled={start.isPending || (t.requires_code && !t.open_attempt_id && !code)} onClick={() => start.mutate()}>{start.isPending ? 'Opening…' : t.open_attempt_id ? 'Resume test' : 'Start test'}</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Runner({ initial, onExit }: { initial: Payload; onExit: (attemptId: number) => void }) {
  const qc = useQueryClient();
  const attemptId = initial.attempt_id;
  const offset = new Date(initial.server_time).getTime() - Date.now();
  const [answers, setAnswers] = useState<Record<number, CbtAnswer>>(() => {
    const m: Record<number, CbtAnswer> = {};
    for (const q of initial.questions) { const v = toAnswer(q.answer); if (v !== undefined) m[q.id] = v; }
    return m;
  });
  const [i, setI] = useState(0);
  const [msg, setMsg] = useState<{ tone: 'error' | 'warn' | 'info'; text: string } | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const dirty = useRef(new Set<number>());
  const events = useRef<Ev[]>([]);
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const qs = initial.questions;
  const finished = useRef(false);

  const payloadAnswers = (ids?: number[]) => qs.filter((q) => (ids ? ids.includes(q.id) : true) && answersRef.current[q.id] !== undefined).map((q) => ({ question_id: q.id, answer: fromAnswer(q, answersRef.current[q.id]) }));

  const sync = useCallback(async () => {
    if (finished.current) return;
    const ids = [...dirty.current];
    const evs = events.current.splice(0);
    if (!ids.length && !evs.length) return;
    dirty.current.clear();
    try {
      const r = unwrap<{ rejected: number; status: string; time_left_seconds: number }>(await postAttemptsSyncKeyed(attemptId, { answers: payloadAnswers(ids), ...(evs.length ? { events: evs } : {}) }, newKey()));
      setSavedAt(new Date().toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      if (r.rejected) setMsg({ tone: 'warn', text: `${r.rejected} answer(s) arrived after time was up and were not counted.` });
      if (r.status !== 'in_progress') { finished.current = true; onExit(attemptId); }
    } catch (e) {
      ids.forEach((x) => dirty.current.add(x));
      events.current.unshift(...evs);
      setMsg({ tone: 'warn', text: `Could not save to the server (${errMsg(e)}). Your answers are kept here and will be retried.` });
    }
  }, [attemptId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { const t = setInterval(sync, 20_000); return () => clearInterval(t); }, [sync]);

  useEffect(() => {
    const log = (event: Ev['event']) => {
      const e = { event, at: new Date().toISOString() };
      postAttemptsEvent(attemptId, e).catch(() => events.current.push(e));
    };
    const vis = () => log(document.hidden ? 'app_background' : 'focus');
    const blur = () => log('blur');
    const copy = () => log('copy');
    const off = () => events.current.push({ event: 'disconnect', at: new Date().toISOString() });
    const on = () => { log('reconnect'); sync(); };
    document.addEventListener('visibilitychange', vis);
    window.addEventListener('blur', blur);
    document.addEventListener('copy', copy);
    window.addEventListener('offline', off);
    window.addEventListener('online', on);
    const unload = (e: BeforeUnloadEvent) => { if (!finished.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', unload);
    return () => {
      document.removeEventListener('visibilitychange', vis); window.removeEventListener('blur', blur); document.removeEventListener('copy', copy);
      window.removeEventListener('offline', off); window.removeEventListener('online', on); window.removeEventListener('beforeunload', unload);
    };
  }, [attemptId, sync]);

  const submit = useMutation({
    mutationFn: () => postAttemptsSubmitKeyed(attemptId, { answers: payloadAnswers() }, newKey()),
    onSuccess: () => { finished.current = true; qc.invalidateQueries({ queryKey: ['student-tests'] }); onExit(attemptId); },
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
  });

  const change = (qid: number, v: CbtAnswer) => { setAnswers((p) => ({ ...p, [qid]: v })); dirty.current.add(qid); };
  const done = qs.filter((q) => isAnswered(answers[q.id])).length;
  const q = qs[i];

  return (
    <Card>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="font-semibold">{initial.title}</h2>
          <p className="text-xs text-slate-500">{done} of {qs.length} answered{savedAt ? ` · saved ${savedAt}` : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => sync()}>Save now</Button>
          <Countdown deadline={initial.deadline} offsetMs={offset} onExpire={() => { if (!submit.isPending && !finished.current) submit.mutate(); }} />
        </div>
      </div>
      {msg && <div className="mb-3"><Banner tone={msg.tone}>{msg.text}</Banner></div>}
      {q ? (
        <>
          <CbtQuestion q={q} index={i} value={answers[q.id]} onChange={(v) => change(q.id, v)} disabled={submit.isPending} />
          <div className="flex justify-between mt-4 gap-2">
            <Button variant="ghost" disabled={i === 0} onClick={() => { sync(); setI(i - 1); }}>Previous</Button>
            {i < qs.length - 1 ? <Button onClick={() => { sync(); setI(i + 1); }}>Next</Button> : (
              <Button variant="danger" disabled={submit.isPending} onClick={async () => { if (await askConfirm(done < qs.length ? `${qs.length - done} question(s) unanswered. Submit anyway?` : 'Submit your test now?', { title: 'Submit test', confirmLabel: 'Submit', destructive: false })) submit.mutate(); }}>{submit.isPending ? 'Submitting…' : 'Submit test'}</Button>
            )}
          </div>
          <div className="mt-4 border-t pt-4"><QuestionNav count={qs.length} current={i} answered={(n) => isAnswered(answers[qs[n].id])} onPick={(n) => { sync(); setI(n); }} /></div>
        </>
      ) : <p className="text-sm text-slate-500">This test has no questions.</p>}
    </Card>
  );
}

export function TestsPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<TestRow | null>(null);
  const [running, setRunning] = useState<Payload | null>(null);
  const tests = useQuery({ queryKey: ['student-tests'], queryFn: () => getTests().then((r) => asList<TestRow>(unwrap(r))), retry: false, enabled: !running });
  // /student/tests?exam=12 opens that test straight away (used by "Take the lesson quiz")
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const want = Number(params.get('exam'));
    const row = want ? (tests.data ?? []).find((t) => t.id === want) : undefined;
    if (row) { setPicked(row); setParams({}, { replace: true }); }
  }, [tests.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resume an open attempt directly from the list (no code needed for a resume).
  const resume = useMutation({ mutationFn: (attemptId: number) => getAttemptsById(attemptId).then((r) => unwrap<Payload>(r)), onSuccess: (p) => (p.status === 'in_progress' ? setRunning(p) : nav(`/student/tests/attempts/${p.attempt_id}/result`)) });

  if (running) return <div className="space-y-4"><PageHeader title="Test in progress" /><Runner initial={running} onExit={(id) => { setRunning(null); qc.invalidateQueries({ queryKey: ['student-tests'] }); nav(`/student/tests/attempts/${id}/result`); }} /></div>;

  return (
    <div className="space-y-4">
      <PageHeader title="Tests & exams" subtitle="Computer-based tests set by your teachers." />
      {resume.isError && <Banner>{errMsg(resume.error)}</Banner>}
      <Card>
        <QueryState q={tests}>
          <Table rows={tests.data ?? []} rowKey={(t) => t.id} empty="There are no tests for you right now." cols={[
            { key: 'title', header: 'Test', render: (t) => <><div className="font-medium">{t.title}</div><div className="text-xs text-slate-500 capitalize">{t.kind} · {t.duration_minutes} min · {t.total_marks} marks</div></> },
            { key: 'window', header: 'Window', render: (t) => <span className="text-xs">{t.opens_at ? fmtDateTime(t.opens_at) : 'Open'}{t.closes_at ? ` – ${fmtDateTime(t.closes_at)}` : ''}</span> },
            { key: 'status', header: 'Status', render: (t) => <StatusBadge status={t.status} /> },
            { key: 'attempts', header: 'Attempts', render: (t) => `${t.attempts_used}/${t.max_attempts}` },
            { key: 'action', header: '', render: (t) => (
              <div className="flex gap-2 justify-end">
                {t.open_attempt_id ? <Button disabled={resume.isPending} onClick={() => resume.mutate(t.open_attempt_id!)}>Resume</Button> : t.can_start ? <Button onClick={() => setPicked(t)}>Start</Button> : null}
                {t.last_attempt_id && !t.open_attempt_id && <Link to={`/student/tests/attempts/${t.last_attempt_id}/result`} className="text-sm font-semibold text-brand-600 self-center">Result</Link>}
              </div>
            ) },
          ]} />
        </QueryState>
      </Card>
      {picked && <StartDialog test={picked} onClose={() => setPicked(null)} onStarted={(p) => { setPicked(null); setRunning(p); }} />}
    </div>
  );
}
